import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Checkbox, Empty, Input, Modal, Spin, Tooltip, Typography } from 'antd';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import { Tag } from '@app/shared/ui/Tag';
import type { TagItemData } from '@app/shared/ui/Tag';

import type { TagsPickerModalProps } from '../types';
import type { FC } from 'react';

import './TagsPickerModal.scss';

const { Text } = Typography;

const LIST_HEIGHT = 360;
const ROW_HEIGHT = 42;
const ROW_OVERSCAN = 6;

interface TagsVirtualListProps {
  items: TagItemData[];
  selectedTagIds: Set<number>;
  isDisabled: boolean;
  onToggleTag(tagId: number): void;
}

interface TagRowProps {
  item: TagItemData;
  isSelected: boolean;
  isDisabled: boolean;
  onToggleTag(tagId: number): void;
}

const getTagId = ({ id }: TagItemData): number | null => {
  const tagId = typeof id === 'number' ? id : Number(id);

  return Number.isFinite(tagId) ? tagId : null;
};

const getFilteredTags = (
  items: TagItemData[],
  searchValue: string
): TagItemData[] => {
  const normalizedSearch = searchValue.trim().toLocaleLowerCase();

  if (!normalizedSearch) return items;

  return items.filter(({ label }) =>
    label.toLocaleLowerCase().includes(normalizedSearch)
  );
};

const TagRow: FC<TagRowProps> = ({
  item,
  isSelected,
  isDisabled,
  onToggleTag,
}) => {
  const tagId = getTagId(item);
  const isRowDisabled = isDisabled || item.disabled || tagId === null;

  const rowClickHandler = useCallback(() => {
    if (isRowDisabled || tagId === null) return;

    onToggleTag(tagId);
  }, [isRowDisabled, onToggleTag, tagId]);

  return (
    <div
      className={cn('TagsPickerModal__row', {
        'TagsPickerModal__row--disabled': isRowDisabled,
      })}
      onClick={rowClickHandler}
      role="button"
      tabIndex={isRowDisabled ? -1 : 0}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        rowClickHandler();
      }}
    >
      <div className="TagsPickerModal__checkbox-cell">
        <Checkbox
          checked={isSelected}
          disabled={isRowDisabled}
          onClick={(event) => event.stopPropagation()}
          onChange={rowClickHandler}
        />
      </div>
      <div className="TagsPickerModal__tag-cell">
        <Tag className="TagsPickerModal__tag" item={item} />
      </div>
      <div className="TagsPickerModal__info-cell">
        {item.description && (
          <Tooltip title={item.description}>
            <InfoCircleOutlined className="TagsPickerModal__info-icon" />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const TagsVirtualList: FC<TagsVirtualListProps> = ({
  items,
  selectedTagIds,
  isDisabled,
  onToggleTag,
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const totalHeight = items.length * ROW_HEIGHT;
  const visibleRowsCount = Math.ceil(LIST_HEIGHT / ROW_HEIGHT);
  const maxStartIndex = Math.max(0, items.length - visibleRowsCount);
  const startIndex = Math.min(
    maxStartIndex,
    Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - ROW_OVERSCAN)
  );
  const endIndex = Math.min(
    items.length,
    startIndex + visibleRowsCount + ROW_OVERSCAN * 2
  );
  const visibleItems = items.slice(startIndex, endIndex);
  const offsetTop = startIndex * ROW_HEIGHT;

  if (items.length === 0) {
    return (
      <div className="TagsPickerModal__empty">
        <Empty
          description="Теги не найдены"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div
      className="TagsPickerModal__list"
      onScroll={({ currentTarget: { scrollTop } }) => setScrollTop(scrollTop)}
    >
      <div className="TagsPickerModal__spacer" style={{ height: totalHeight }}>
        <div
          className="TagsPickerModal__visible-rows"
          style={{ transform: `translateY(${offsetTop}px)` }}
        >
          {visibleItems.map((item) => {
            const tagId = getTagId(item);

            return (
              <TagRow
                item={item}
                isSelected={tagId !== null && selectedTagIds.has(tagId)}
                isDisabled={isDisabled}
                onToggleTag={onToggleTag}
                key={item.id}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const TagsPickerModal: FC<TagsPickerModalProps> = ({
  open,
  title,
  okText,
  cancelText = 'Отменить',
  availableTags,
  selectedTagIds,
  isTagsLoading = false,
  isSaving = false,
  isConfirmDisabled = false,
  onSelectedTagIdsChange,
  onConfirm,
  onCancel,
}) => {
  const [searchValue, setSearchValue] = useState('');

  const selectedTagIdsSet = useMemo(
    () => new Set(selectedTagIds),
    [selectedTagIds]
  );

  const filteredTags = useMemo(
    () => getFilteredTags(availableTags, searchValue),
    [availableTags, searchValue]
  );

  const toggleTagHandler = useCallback(
    (tagId: number) => {
      const nextTagIds = new Set(selectedTagIdsSet);

      if (nextTagIds.has(tagId)) {
        nextTagIds.delete(tagId);
      } else {
        nextTagIds.add(tagId);
      }

      onSelectedTagIdsChange(Array.from(nextTagIds));
    },
    [onSelectedTagIdsChange, selectedTagIdsSet]
  );

  const cancelHandler = useCallback(() => {
    if (isSaving) return;

    setSearchValue('');
    onCancel();
  }, [isSaving, onCancel]);

  return (
    <Modal
      title={title}
      okText={okText}
      cancelText={cancelText}
      className="TagsPickerModal"
      open={open}
      onOk={onConfirm}
      onCancel={cancelHandler}
      afterClose={() => setSearchValue('')}
      okButtonProps={{
        loading: isSaving,
        disabled: isConfirmDisabled,
      }}
      cancelButtonProps={{ disabled: isSaving }}
      destroyOnHidden
    >
      <div className="TagsPickerModal__content">
        <Input
          className="TagsPickerModal__search"
          prefix={<SearchOutlined />}
          placeholder="Введите название тега"
          value={searchValue}
          disabled={isSaving}
          onChange={({ target: { value } }) => setSearchValue(value)}
        />

        <div className="TagsPickerModal__meta">
          <Text type="secondary">Выбрано тегов: {selectedTagIds.length}</Text>
          <Text type="secondary">Показано: {filteredTags.length}</Text>
        </div>

        {isTagsLoading ? (
          <div className="TagsPickerModal__loader">
            <Spin />
          </div>
        ) : (
          <TagsVirtualList
            key={searchValue}
            items={filteredTags}
            selectedTagIds={selectedTagIdsSet}
            isDisabled={isSaving}
            onToggleTag={toggleTagHandler}
          />
        )}
      </div>
    </Modal>
  );
};
